/**
 * SupportDashboard - Admin support ticket management
 * Phase 11 Part 2: Support System
 */
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import api from '../lib/api';
import ChatWindow from '../components/ChatWindow';
import { 
  ArrowLeft, 
  Headphones, 
  Package,
  CreditCard,
  AlertTriangle,
  HelpCircle,
  MessageCircle,
  Clock,
  CheckCircle,
  XCircle,
  ChevronRight,
  Loader2,
  Users,
  Filter,
  Search,
  UserCheck,
  BarChart3,
  RefreshCw
} from 'lucide-react';

// Ticket categories
const CATEGORIES = [
  { id: 'delivery_issue', label: 'Delivery', icon: Package, color: 'text-blue-500' },
  { id: 'payment_issue', label: 'Payment', icon: CreditCard, color: 'text-green-500' },
  { id: 'order_issue', label: 'Order', icon: Package, color: 'text-purple-500' },
  { id: 'fraud_suspicion', label: 'Fraud', icon: AlertTriangle, color: 'text-red-500' },
  { id: 'app_support', label: 'App', icon: HelpCircle, color: 'text-gray-500' },
  { id: 'other', label: 'Other', icon: MessageCircle, color: 'text-gray-400' }
];

// Status colors (matching user specs)
const STATUS_COLORS = {
  open: { bg: 'bg-[#FFD300]', text: 'text-black', label: 'Open' },
  in_progress: { bg: 'bg-[#00A86B]', text: 'text-white', label: 'In Progress' },
  resolved: { bg: 'bg-gray-200', text: 'text-gray-700', label: 'Resolved' },
  closed: { bg: 'bg-gray-100', text: 'text-gray-500', label: 'Closed' }
};

// Priority colors (matching user specs)
const PRIORITY_COLORS = {
  low: { bg: 'bg-gray-100', text: 'text-gray-600', label: 'Low' },
  medium: { bg: 'bg-blue-100', text: 'text-blue-600', label: 'Medium' },
  high: { bg: 'bg-orange-100', text: 'text-orange-600', label: 'High' },
  critical: { bg: 'bg-red-500', text: 'text-white', label: 'Critical' }
};

export default function SupportDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [tickets, setTickets] = useState([]);
  const [stats, setStats] = useState(null);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [admins, setAdmins] = useState([]);
  
  // Filters
  const [statusFilter, setStatusFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  const fetchTickets = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);
      if (priorityFilter) params.append('priority', priorityFilter);
      if (categoryFilter) params.append('category', categoryFilter);
      
      const res = await api.get(`/support/tickets?${params}`);
      setTickets(res.data.tickets);
    } catch (error) {
      toast.error('Failed to load tickets');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, priorityFilter, categoryFilter]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await api.get('/support/dashboard/stats');
      setStats(res.data);
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  }, []);

  const fetchAdmins = useCallback(async () => {
    try {
      const res = await api.get('/admin/users?role=ADMIN');
      setAdmins(res.data.users || []);
    } catch (error) {
      console.error('Failed to load admins:', error);
    }
  }, []);

  useEffect(() => {
    fetchTickets();
    fetchStats();
    fetchAdmins();
  }, [fetchTickets, fetchStats, fetchAdmins]);

  const updateTicket = async (ticketId, updates) => {
    try {
      await api.patch(`/support/tickets/${ticketId}`, updates);
      toast.success('Ticket updated');
      fetchTickets();
      fetchStats();
      
      if (selectedTicket?.id === ticketId) {
        setSelectedTicket(prev => ({ ...prev, ...updates }));
      }
    } catch (error) {
      toast.error('Failed to update ticket');
    }
  };

  const assignToMe = (ticketId) => {
    updateTicket(ticketId, { 
      assigned_to_admin: user.id,
      status: 'in_progress'
    });
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleDateString([], { 
      month: 'short', 
      day: 'numeric', 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  // Filter tickets by search
  const filteredTickets = tickets.filter(ticket => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      ticket.subject?.toLowerCase().includes(query) ||
      ticket.id?.toLowerCase().includes(query) ||
      ticket.created_by_name?.toLowerCase().includes(query)
    );
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#00A86B]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-gradient-to-r from-black to-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => selectedTicket ? setSelectedTicket(null) : navigate('/admin')}
              className="text-white hover:bg-white/20"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex-1">
              <h1 className="text-xl font-bold flex items-center gap-2">
                <Headphones className="w-6 h-6 text-[#FFD300]" />
                Support Dashboard
              </h1>
              <p className="text-sm text-white/70">
                Manage customer support tickets
              </p>
            </div>
            <Button 
              onClick={() => { fetchTickets(); fetchStats(); }}
              variant="outline"
              className="border-white/30 text-white hover:bg-white/10"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <Card className="p-4 border-l-4 border-l-[#FFD300]">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500 uppercase">Open</p>
                  <p className="text-2xl font-bold text-gray-800">{stats.by_status?.open || 0}</p>
                </div>
                <div className="w-10 h-10 rounded-full bg-[#FFD300]/20 flex items-center justify-center">
                  <Clock className="w-5 h-5 text-[#FFD300]" />
                </div>
              </div>
            </Card>
            
            <Card className="p-4 border-l-4 border-l-[#00A86B]">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500 uppercase">In Progress</p>
                  <p className="text-2xl font-bold text-gray-800">{stats.by_status?.in_progress || 0}</p>
                </div>
                <div className="w-10 h-10 rounded-full bg-[#00A86B]/20 flex items-center justify-center">
                  <MessageCircle className="w-5 h-5 text-[#00A86B]" />
                </div>
              </div>
            </Card>
            
            <Card className="p-4 border-l-4 border-l-red-500">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500 uppercase">Critical</p>
                  <p className="text-2xl font-bold text-red-600">{stats.by_priority?.critical || 0}</p>
                </div>
                <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-red-500" />
                </div>
              </div>
            </Card>
            
            <Card className="p-4 border-l-4 border-l-blue-500">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500 uppercase">Unassigned</p>
                  <p className="text-2xl font-bold text-gray-800">{stats.unassigned_count || 0}</p>
                </div>
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                  <Users className="w-5 h-5 text-blue-500" />
                </div>
              </div>
            </Card>
          </div>
        )}

        <div className="flex gap-6">
          {/* Ticket List */}
          <div className={`flex-1 ${selectedTicket ? 'hidden lg:block lg:w-1/2' : 'w-full'}`}>
            {/* Filters */}
            <Card className="p-4 mb-4">
              <div className="flex flex-wrap gap-3">
                <div className="relative flex-1 min-w-[200px]">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search tickets..."
                    className="pl-9"
                  />
                </div>
                
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="px-3 py-2 border rounded-lg text-sm"
                >
                  <option value="">All Status</option>
                  <option value="open">Open</option>
                  <option value="in_progress">In Progress</option>
                  <option value="resolved">Resolved</option>
                  <option value="closed">Closed</option>
                </select>
                
                <select
                  value={priorityFilter}
                  onChange={(e) => setPriorityFilter(e.target.value)}
                  className="px-3 py-2 border rounded-lg text-sm"
                >
                  <option value="">All Priority</option>
                  <option value="critical">Critical</option>
                  <option value="high">High</option>
                  <option value="medium">Medium</option>
                  <option value="low">Low</option>
                </select>
                
                <select
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                  className="px-3 py-2 border rounded-lg text-sm"
                >
                  <option value="">All Categories</option>
                  {CATEGORIES.map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.label}</option>
                  ))}
                </select>
              </div>
            </Card>

            {/* Tickets */}
            <div className="space-y-3">
              {filteredTickets.length === 0 ? (
                <Card className="p-12 text-center">
                  <Headphones className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-800 mb-2">No Tickets Found</h3>
                  <p className="text-gray-500">Adjust your filters or wait for new tickets.</p>
                </Card>
              ) : (
                filteredTickets.map(ticket => {
                  const Cat = CATEGORIES.find(c => c.id === ticket.category);
                  const CatIcon = Cat?.icon || HelpCircle;
                  const statusStyle = STATUS_COLORS[ticket.status] || STATUS_COLORS.open;
                  const priorityStyle = PRIORITY_COLORS[ticket.priority] || PRIORITY_COLORS.medium;
                  const isSelected = selectedTicket?.id === ticket.id;
                  
                  return (
                    <Card
                      key={ticket.id}
                      onClick={() => setSelectedTicket(ticket)}
                      className={`p-4 cursor-pointer transition-all hover:border-[#00A86B]/50 ${
                        isSelected ? 'border-[#00A86B] bg-[#00A86B]/5' : ''
                      } ${ticket.priority === 'critical' ? 'border-l-4 border-l-red-500' : ''}`}
                    >
                      <div className="flex items-start gap-4">
                        <div className={`w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0`}>
                          <CatIcon className={`w-5 h-5 ${Cat?.color || 'text-gray-500'}`} />
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="font-semibold text-gray-800 truncate">{ticket.subject}</h3>
                            <Badge className={`${statusStyle.bg} ${statusStyle.text} text-xs`}>
                              {statusStyle.label}
                            </Badge>
                            <Badge className={`${priorityStyle.bg} ${priorityStyle.text} text-xs`}>
                              {priorityStyle.label}
                            </Badge>
                          </div>
                          
                          <div className="flex items-center gap-3 mt-2 text-sm text-gray-500">
                            <span>#{ticket.id.slice(0, 8)}</span>
                            <span>•</span>
                            <span>{ticket.created_by_name}</span>
                            <span>•</span>
                            <span>{formatTime(ticket.created_at)}</span>
                          </div>
                          
                          <div className="flex items-center gap-2 mt-2">
                            {ticket.assigned_admin_name ? (
                              <Badge className="bg-[#00A86B]/10 text-[#00A86B] text-xs">
                                <UserCheck className="w-3 h-3 mr-1" />
                                {ticket.assigned_admin_name}
                              </Badge>
                            ) : (
                              <Button
                                size="sm"
                                onClick={(e) => { e.stopPropagation(); assignToMe(ticket.id); }}
                                className="h-6 text-xs bg-[#00A86B] hover:bg-[#00A86B]/90"
                              >
                                Assign to me
                              </Button>
                            )}
                            
                            {ticket.message_count > 0 && (
                              <span className="text-xs text-gray-400 flex items-center gap-1">
                                <MessageCircle className="w-3 h-3" />
                                {ticket.message_count}
                              </span>
                            )}
                          </div>
                        </div>
                        
                        <ChevronRight className="w-5 h-5 text-gray-300 flex-shrink-0" />
                      </div>
                    </Card>
                  );
                })
              )}
            </div>
          </div>

          {/* Selected Ticket Chat */}
          {selectedTicket && (
            <div className="flex-1 lg:w-1/2">
              {/* Ticket Controls */}
              <Card className="p-4 mb-4">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="font-semibold text-gray-800">{selectedTicket.subject}</h3>
                    <p className="text-sm text-gray-500">#{selectedTicket.id.slice(0, 8)} • {selectedTicket.created_by_name}</p>
                  </div>
                  <Button 
                    variant="ghost"
                    size="sm"
                    onClick={() => setSelectedTicket(null)}
                    className="lg:hidden"
                  >
                    <XCircle className="w-4 h-4" />
                  </Button>
                </div>
                
                <div className="flex flex-wrap gap-2">
                  {/* Status dropdown */}
                  <select
                    value={selectedTicket.status}
                    onChange={(e) => updateTicket(selectedTicket.id, { status: e.target.value })}
                    className="px-3 py-1.5 border rounded-lg text-sm"
                  >
                    <option value="open">Open</option>
                    <option value="in_progress">In Progress</option>
                    <option value="resolved">Resolved</option>
                    <option value="closed">Closed</option>
                  </select>
                  
                  {/* Priority dropdown */}
                  <select
                    value={selectedTicket.priority}
                    onChange={(e) => updateTicket(selectedTicket.id, { priority: e.target.value })}
                    className="px-3 py-1.5 border rounded-lg text-sm"
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="critical">Critical</option>
                  </select>
                  
                  {/* Assign dropdown */}
                  <select
                    value={selectedTicket.assigned_to_admin || ''}
                    onChange={(e) => updateTicket(selectedTicket.id, { assigned_to_admin: e.target.value || null })}
                    className="px-3 py-1.5 border rounded-lg text-sm"
                  >
                    <option value="">Unassigned</option>
                    {admins.map(admin => (
                      <option key={admin.id} value={admin.id}>{admin.name}</option>
                    ))}
                  </select>
                </div>
              </Card>

              {/* Chat */}
              <ChatWindow
                conversationId={selectedTicket.conversation_id}
                title={`Support: ${selectedTicket.subject}`}
                className="h-[500px]"
              />
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
